'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Notification {
  id: string;
  userId: string;
  category: 'high_value_transaction' | 'card_freeze' | 'kyc_update' | 'security_event' | 'general';
  title: string;
  message: string;
  isRead: boolean;
  metadata: Record<string, any>;
  createdAt: string;
}

export interface AlertPreferences {
  id: string;
  userId: string;
  highValueTransaction: boolean;
  highValueThreshold: number;
  cardFreeze: boolean;
  kycUpdate: boolean;
  securityEvent: boolean;
  general: boolean;
}

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  preferences: AlertPreferences | null;
  loading: boolean;
  prefsLoading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismissNotification: (id: string) => Promise<void>;
  updatePreferences: (prefs: Partial<AlertPreferences>) => Promise<void>;
  addNotification: (notif: Omit<Notification, 'id' | 'createdAt' | 'isRead'>) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};

function mapRow(row: any): Notification {
  return {
    id: row.id,
    userId: row.user_id,
    category: row.category,
    title: row.title,
    message: row.message,
    isRead: row.is_read,
    metadata: row.metadata || {},
    createdAt: row.created_at,
  };
}

function mapPrefsRow(row: any): AlertPreferences {
  return {
    id: row.id,
    userId: row.user_id,
    highValueTransaction: row.high_value_transaction,
    highValueThreshold: Number(row.high_value_threshold),
    cardFreeze: row.card_freeze,
    kycUpdate: row.kyc_update,
    securityEvent: row.security_event,
    general: row.general,
  };
}

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [preferences, setPreferences] = useState<AlertPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const channelRef = useRef<any>(null);

  const supabase = createClient();

  const fetchNotifications = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);
      if (!error && data) {
        setNotifications(data.map(mapRow));
      }
    } catch (err) {
      console.log('Fetch notifications error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPreferences = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('alert_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      if (!error && data) {
        setPreferences(mapPrefsRow(data));
      } else if (!error && !data) {
        // Create default prefs if missing
        const { data: newPrefs } = await supabase
          .from('alert_preferences')
          .insert({ user_id: userId })
          .select()
          .single();
        if (newPrefs) setPreferences(mapPrefsRow(newPrefs));
      }
    } catch (err) {
      console.log('Fetch preferences error:', err);
    } finally {
      setPrefsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      setPrefsLoading(false);
      return;
    }

    fetchNotifications(user.id);
    fetchPreferences(user.id);

    // Real-time subscription
    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotif = mapRow(payload.new);
          setNotifications((prev) => [newNotif, ...prev]);
          // Browser notification if permission granted
          if (typeof window !== 'undefined' && 'Notification' in window && window.Notification.permission === 'granted') {
            new window.Notification(newNotif.title, {
              body: newNotif.message,
              icon: '/favicon.ico',
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          setNotifications((prev) =>
            prev.map((n) => (n.id === payload.new.id ? mapRow(payload.new) : n))
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          setNotifications((prev) => prev.filter((n) => n.id !== payload.old.id));
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user?.id]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await supabase.from('notifications').update({ is_read: true }).eq('id', id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
    } catch (err) {
      console.log('Mark as read error:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (err) {
      console.log('Mark all as read error:', err);
    }
  }, [user?.id]);

  const dismissNotification = useCallback(async (id: string) => {
    try {
      await supabase.from('notifications').delete().eq('id', id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      console.log('Dismiss notification error:', err);
    }
  }, []);

  const updatePreferences = useCallback(async (prefs: Partial<AlertPreferences>) => {
    if (!user?.id || !preferences) return;
    const dbPrefs: Record<string, any> = {};
    if (prefs.highValueTransaction !== undefined) dbPrefs.high_value_transaction = prefs.highValueTransaction;
    if (prefs.highValueThreshold !== undefined) dbPrefs.high_value_threshold = prefs.highValueThreshold;
    if (prefs.cardFreeze !== undefined) dbPrefs.card_freeze = prefs.cardFreeze;
    if (prefs.kycUpdate !== undefined) dbPrefs.kyc_update = prefs.kycUpdate;
    if (prefs.securityEvent !== undefined) dbPrefs.security_event = prefs.securityEvent;
    if (prefs.general !== undefined) dbPrefs.general = prefs.general;
    dbPrefs.updated_at = new Date().toISOString();

    try {
      const { data } = await supabase
        .from('alert_preferences')
        .update(dbPrefs)
        .eq('user_id', user.id)
        .select()
        .single();
      if (data) setPreferences(mapPrefsRow(data));
    } catch (err) {
      console.log('Update preferences error:', err);
    }
  }, [user?.id, preferences]);

  const addNotification = useCallback(async (notif: Omit<Notification, 'id' | 'createdAt' | 'isRead'>) => {
    if (!user?.id) return;
    try {
      await supabase.from('notifications').insert({
        user_id: notif.userId || user.id,
        category: notif.category,
        title: notif.title,
        message: notif.message,
        metadata: notif.metadata || {},
      });
    } catch (err) {
      console.log('Add notification error:', err);
    }
  }, [user?.id]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        preferences,
        loading,
        prefsLoading,
        markAsRead,
        markAllAsRead,
        dismissNotification,
        updatePreferences,
        addNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
